import { BookOpen, ExternalLink, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { CourtRuleAuthoritySource, RuntimeAnswer } from '../../types/runtime';

interface Props {
  answer: RuntimeAnswer;
  onOpenSource?: (source: CourtRuleAuthoritySource) => void;
}

const STATUS_LABELS: Record<string, string> = {
  exact_mention_search: 'Exact mention search',
  governs: 'Local rule governs',
  local_rule_governs: 'Local rule governs',
  mixed_authority: 'Mixed authority',
  no_direct_local_rule: 'No direct local rule',
  no_direct_rule_found: 'No direct local rule',
  no_trusted_rule_found: 'No trusted rule',
  possibly_governs: 'Related rules',
  related_rules: 'Related rules',
};

function readableStatus(value: string | null): string | null {
  if (!value) {
    return null;
  }
  return STATUS_LABELS[value] ?? value.replace(/_/g, ' ');
}

const SOURCE_ROLE_LABELS: Record<string, string> = {
  exact_mention: 'Exact mention',
  governing_local_rule: 'Governing local rule',
};

function sourceRoleLabel(value: string | null | undefined): string {
  if (!value) {
    return 'Approved source';
  }
  return SOURCE_ROLE_LABELS[value] ?? value.replace(/_/g, ' ');
}

function sourceTitle(source: CourtRuleAuthoritySource): string {
  return (
    source.citation_label ||
    source.rule_heading ||
    source.source_artifact_title ||
    'Court Rules source'
  );
}

function sourceHref(source: CourtRuleAuthoritySource): string | null {
  if (source.is_clickable === false) {
    return null;
  }
  const href = source.viewer_url || source.dms_viewer_url || source.source_url;
  return href && href.trim() ? href : null;
}

function hasPanelSource(source: CourtRuleAuthoritySource): boolean {
  return Boolean(source.dms_document_id && source.dms_document_version_id);
}

function SourceReference({
  source,
  onOpenSource,
}: {
  source: CourtRuleAuthoritySource;
  onOpenSource?: (source: CourtRuleAuthoritySource) => void;
}) {
  const title = sourceTitle(source);
  const href = sourceHref(source);
  const role = sourceRoleLabel(source.authority_role);
  const pageLabel = source.page_label;
  const sourceSubtitle = source.source_artifact_title || source.jurisdiction_code || null;
  const content = (
    <>
      <span className="flex min-w-0 items-center gap-1.5">
        <BookOpen aria-hidden="true" className="h-3.5 w-3.5 flex-none text-sky-700" />
        <span className="min-w-0 truncate font-medium text-sky-950">{title}</span>
      </span>
      <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] leading-4 text-sky-800">
        <span>{role}</span>
        {pageLabel ? <span>{pageLabel}</span> : null}
        {sourceSubtitle ? <span className="max-w-full truncate">{sourceSubtitle}</span> : null}
      </span>
    </>
  );

  if (onOpenSource && hasPanelSource(source)) {
    return (
      <button
        type="button"
        onClick={() => onOpenSource(source)}
        className="group flex min-w-0 max-w-full flex-col rounded-md border border-sky-200 bg-white/80 px-3 py-2 text-left shadow-sm transition hover:border-sky-300 hover:bg-white focus:outline-none focus:ring-2 focus:ring-sky-300"
      >
        <span className="flex min-w-0 items-start justify-between gap-3">
          <span className="min-w-0 flex-1">{content}</span>
          <ExternalLink
            aria-hidden="true"
            className="mt-0.5 h-3.5 w-3.5 flex-none text-sky-500 transition group-hover:text-sky-700"
          />
        </span>
      </button>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="group flex min-w-0 max-w-full flex-col rounded-md border border-sky-200 bg-white/80 px-3 py-2 text-left shadow-sm transition hover:border-sky-300 hover:bg-white focus:outline-none focus:ring-2 focus:ring-sky-300"
      >
        <span className="flex min-w-0 items-start justify-between gap-3">
          <span className="min-w-0 flex-1">{content}</span>
          <ExternalLink
            aria-hidden="true"
            className="mt-0.5 h-3.5 w-3.5 flex-none text-sky-500 transition group-hover:text-sky-700"
          />
        </span>
      </a>
    );
  }

  return (
    <div className="flex min-w-0 max-w-full flex-col rounded-md border border-sky-100 bg-white/60 px-3 py-2 text-left">
      {content}
    </div>
  );
}

export function CourtRulesAnswerCard({ answer, onOpenSource }: Props) {
  const status =
    typeof answer.court_rule_display_status === 'string'
      ? answer.court_rule_display_status
      : typeof answer.court_rule_governance_status === 'string'
        ? answer.court_rule_governance_status
        : null;
  const statusLabel = readableStatus(status);
  const hasDisplayCountLabel = typeof answer.court_rule_display_count_label === 'string';
  const countLabel =
    hasDisplayCountLabel
      ? answer.court_rule_display_count_label
      : typeof answer.candidate_rule_count === 'number'
        ? `${answer.candidate_rule_count} candidate${answer.candidate_rule_count === 1 ? '' : 's'}`
        : null;
  const authoritySources = Array.isArray(answer.court_rule_authority_sources)
    ? answer.court_rule_authority_sources
    : [];

  return (
    <article className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-950">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="rounded bg-sky-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-900">
          Court Rules
        </span>
        {statusLabel ? <span className="text-xs text-sky-800">{statusLabel}</span> : null}
        {countLabel ? <span className="text-xs text-sky-800">{countLabel}</span> : null}
      </div>
      <div className="space-y-3 leading-relaxed text-sky-950">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => (
              <h1 className="text-base font-semibold leading-snug text-sky-950">{children}</h1>
            ),
            h2: ({ children }) => (
              <h2 className="mt-4 border-t border-sky-200 pt-3 text-sm font-semibold leading-snug text-sky-950">
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 className="mt-3 text-sm font-semibold leading-snug text-sky-900">{children}</h3>
            ),
            p: ({ children }) => <p className="leading-relaxed">{children}</p>,
            ul: ({ children }) => (
              <ul className="ml-5 list-disc space-y-1 leading-relaxed">{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className="ml-5 list-decimal space-y-1 leading-relaxed">{children}</ol>
            ),
            li: ({ children }) => <li className="pl-1">{children}</li>,
            strong: ({ children }) => (
              <strong className="font-semibold text-sky-950">{children}</strong>
            ),
            em: ({ children }) => <em className="text-sky-900">{children}</em>,
            hr: () => <hr className="border-sky-200" />,
            code: ({ children }) => (
              <code className="rounded bg-sky-100 px-1 py-0.5 text-[0.85em] text-sky-950">
                {children}
              </code>
            ),
            a: ({ children, href }) => (
              <a
                href={href}
                className="font-medium text-sky-700 underline underline-offset-2"
                target="_blank"
                rel="noreferrer"
              >
                {children}
              </a>
            ),
          }}
        >
          {answer.content}
        </ReactMarkdown>
      </div>
      {authoritySources.length > 0 ? (
        <section className="mt-4 border-t border-sky-200 pt-3">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-sky-950">
            <FileText aria-hidden="true" className="h-3.5 w-3.5 text-sky-700" />
            <span>Rule sources</span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {authoritySources.map((source, index) => (
              <SourceReference
                // Source ids can repeat across roles in comparison packets; keep the key stable per render.
                key={`${source.authority_role ?? 'source'}-${source.source_chunk_id ?? source.citation_label ?? index}-${index}`}
                source={source}
                onOpenSource={onOpenSource}
              />
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}
